import { SERVICE_TYPE_OPTIONS } from "./constants/serviceTypes";

const SERVICE_TYPES = SERVICE_TYPE_OPTIONS;

const normalize = (text) => text.trim().toLowerCase();

const parseNumber = (value) => {
  if (!value) return null;
  const cleaned = value.replace(/[,\$]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const extractMileage = (text) => {
  const match = text.match(/(\d{1,3}(?:[\d,]{0,})?)\s*(?:miles|mi)\b/i);
  return match ? parseNumber(match[1]) : null;
};

const extractCost = (text) => {
  const match = text.match(/\$\s*(\d{1,3}(?:[\d,]*(?:\.\d{1,2})?))/);
  if (match) return parseNumber(match[1]);
  const looseMatch = text.match(/(?:cost|paid)\s*(?:about|around|of)?\s*\$?\s*(\d{1,3}(?:[\d,]*(?:\.\d{1,2})?))/i);
  return looseMatch ? parseNumber(looseMatch[1]) : null;
};

const extractDate = (text) => {
  if (/\btoday\b/i.test(text)) {
    return new Date().toISOString().split("T")[0];
  }
  if (/\byesterday\b/i.test(text)) {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  }

  const isoMatch = text.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoMatch) return isoMatch[1];

  const usMatch = text.match(/\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})\b/);
  if (usMatch) {
    const month = Number(usMatch[1]).toString().padStart(2, "0");
    const day = Number(usMatch[2]).toString().padStart(2, "0");
    const year = Number(usMatch[3]) < 100 ? `20${usMatch[3]}` : usMatch[3];
    return `${year}-${month}-${day}`;
  }

  return null;
};

const findServiceTypeByKeyword = (message) => {
  if (/\boil\b/.test(message)) return "Oil Change";
  if (/\bbrake\b/.test(message) && /pad|replace|changed/.test(message)) return "Brake Pads";
  if (/\btire\b/.test(message)) return /rotation/.test(message) ? "Tire Rotation" : "Tire Change";
  if (/battery/.test(message)) return "Battery Replacement";
  if (/spark/.test(message)) return "Spark Plugs";
  if (/coolant/.test(message)) return "Coolant Flush";
  if (/transmission fluid/.test(message)) return "Transmission Fluid";
  if (/cabin air filter/.test(message)) return "Cabin Air Filter";
  if (/alignment/.test(message)) return "Alignment";
  if (/wiper/.test(message)) return "Wiper Blades";
  if (/brake fluid/.test(message)) return "Brake Fluid";
  if (/air filter/.test(message)) return "Engine Air Filter Replacement";
  if (/headlight/.test(message)) return "Headlight Bulb Replacement";
  if (/windshield/.test(message)) return /crack/.test(message) ? "Windshield Crack Repair" : "Windshield Replacement";
  return null;
};

const parseReminder = (text) => {
  const due = new Date();
  const match = text.match(/in\s*(\d+)\s*(day|days|week|weeks|month|months|year|years)/);
  if (match) {
    const value = Number(match[1]);
    const unit = match[2];
    if (/day/.test(unit)) due.setDate(due.getDate() + value);
    if (/week/.test(unit)) due.setDate(due.getDate() + value * 7);
    if (/month/.test(unit)) due.setMonth(due.getMonth() + value);
    if (/year/.test(unit)) due.setFullYear(due.getFullYear() + value);
    return due.toISOString().split("T")[0];
  }

  const dateMatch = text.match(/on\s*(\d{4}-\d{2}-\d{2})/);
  if (dateMatch) return dateMatch[1];
  return null;
};

const filterRecords = (records = [], filterText) => {
  const normalizedFilter = filterText.toLowerCase();
  return records.filter((record) => {
    const service = String(record.service_type || record.serviceType || "").toLowerCase();
    return service.includes(normalizedFilter);
  });
};

const findMostRecent = (records = []) => {
  if (!records.length) return null;
  return [...records].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
};

export function generateAssistantResponse(userMessage, records = [], vehicleProfile = null, currentMileage = 0) {
  const message = String(userMessage || "");
  const normalized = normalize(message);
  const serviceType = SERVICE_TYPES.find((type) => normalized.includes(type.toLowerCase())) || findServiceTypeByKeyword(normalized);
  const mileage = extractMileage(message);
  const cost = extractCost(message);
  const date = extractDate(message) || new Date().toISOString().split("T")[0];
  const reminderDate = parseReminder(message);

  const isAddRecordIntent = /\b(changed|replaced|installed|did|completed|logged|service|serviced)\b/.test(normalized) && (serviceType || mileage);
  const isAskHistoryIntent = /\b(last|recent|when was|history|last service)\b/.test(normalized);
  const isMaintenancePlanIntent = /\b(what should i|when should i|due|next service|recommend|need to do|maintenance plan)\b/.test(normalized);
  const isSymptomIntent = /\b(shake|shaking|vibration|vibrate|grinding|noise|squeal|pulling|hard to steer|steering wheel)\b/.test(normalized);
  const isReminderIntent = /\b(remind me|reminder|remember to|schedule|notify me)\b/.test(normalized);
  const isSearchIntent = /\b(show me all|list all|all .* services|find .* records)\b/.test(normalized);

  if (isReminderIntent && reminderDate) {
    const titleMatch = message.match(/remind me to\s*(.+?)(?:\sin|\son|$)/i);
    const title = titleMatch ? titleMatch[1].trim() : "Service reminder";
    return {
      intent: "create_reminder",
      text: `Got it. I can set a reminder for ${title} on ${reminderDate}. Confirm to save it.`,
      reminderDraft: {
        title,
        dueDate: reminderDate,
        note: `Reminder created by AutoLogg Assistant for: ${title}`,
      },
    };
  }

  if (isAddRecordIntent && (serviceType || mileage)) {
    const draft = {
      serviceType: serviceType || "Maintenance",
      mileage: mileage || currentMileage || 0,
      date,
      cost,
      notes: `Logged by AutoLogg Assistant from: "${message}"`,
    };
    return {
      intent: "add_record",
      text: `I found a maintenance entry in your message. Please confirm before I add it to your records.`,
      recordDraft: draft,
    };
  }

  if (isAskHistoryIntent && serviceType) {
    const found = filterRecords(records, serviceType);
    if (found.length) {
      const recent = findMostRecent(found);
      return {
        intent: "ask_history",
        text: `Your last ${serviceType} was on ${recent.date} at ${recent.mileage} miles.`, 
      };
    }
    return {
      intent: "ask_history",
      text: `I couldn't find a ${serviceType} record in your history, but I can help log one if needed.`,
    };
  }

  if (isSearchIntent && serviceType) {
    const matches = filterRecords(records, serviceType);
    if (!matches.length) {
      return {
        intent: "search_records",
        text: `I did not find any records for ${serviceType} yet.`, 
      };
    }
    const summary = matches
      .slice(0, 3)
      .map((record) => `• ${record.date} at ${record.mileage} miles`) 
      .join("\n");
    return {
      intent: "search_records",
      text: `I found ${matches.length} ${serviceType} record(s):\n${summary}`,
    };
  }

  if (isMaintenancePlanIntent) {
    const suggestions = [];
    const lastOil = findMostRecent(filterRecords(records, "oil change"));
    const lastTire = findMostRecent(filterRecords(records, "tire rotation"));
    const lastBrake = findMostRecent(filterRecords(records, "brake fluid"));

    if (!lastOil || currentMileage - Number(lastOil.mileage || 0) >= 5000) {
      suggestions.push("Oil change");
    }
    if (!lastTire || currentMileage - Number(lastTire.mileage || 0) >= 6000) {
      suggestions.push("Tire rotation");
    }
    if (!lastBrake) {
      suggestions.push("Brake fluid check");
    }

    if (!suggestions.length) {
      return {
        intent: "maintenance_plan",
        text: "Your recent service history looks healthy. Keep tracking mileage and log any new maintenance as it happens.",
      };
    }

    return {
      intent: "maintenance_plan",
      text: `Based on your records, I recommend: ${suggestions.join(", ")}.`, 
    };
  }

  if (isSymptomIntent) {
    const causes = [
      "worn brake rotors or pads",
      "misaligned tires or wheels",
      "uneven tire wear",
      "loose suspension components",
    ];
    const description = `Your symptom suggests ${causes[0]}. I also found ${records.length} maintenance entries in your history.`;
    return {
      intent: "diagnose_symptom",
      text: description,
      issueDraft: {
        title: "Possible brake or suspension issue",
        description: message,
        mileage: currentMileage,
        possibleCauses: causes,
      },
    };
  }

  if (/\b(help|how|what|when|where|why)\b/.test(normalized)) {
    return {
      intent: "general_question",
      text: "I can help with your car's maintenance history, log new service entries, diagnose symptoms, or plan the next service. Try asking, 'When was my last oil change?' or 'Log an oil change at 42,000 miles.'",
    };
  }

  return {
    intent: "fallback",
    text: "I didn't catch that. You can ask me to log a service, review past maintenance, diagnose a symptom, or set a reminder.",
  };
}
